<template>
  <ValidationObserver v-slot="{ invalid, handleSubmit }" tag="div">
    <form @submit.prevent="handleSubmit(submit)">
      <v-slide-y-transition group>
        <div class="pt-8 px color--nice-black" v-if="ready" key="container">
          <!-- 금액 및 입금자명 입력 -->
          <v-slide-y-transition group tag="div" class="form-group" hide-on-leave>
            <template v-if="isCheckedHolderName">
              <CreditPayInput
                key="amount"
                ref="amount"
                title="외상할 금액"
                :inputValue.sync="form.amount"
                placeholder="외상할 금액을 입력해 주세요"
                :valid="{ name: '외상할 금액', rules: 'required' }"
                oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
              />

              <CreditPayInput
                key="depositName"
                :inputValue.sync="form.depositName"
                :valid="{ name: '입금자명', rules: 'required' }"
                title="입금자명"
                placeholder="입금자명을 입력해 주세요"
              />
            </template>
          </v-slide-y-transition>

          <!-- 계좌 정보 입력 -->
          <div v-if="wholesalerAccount" class="form-group">
            <template v-if="isExistWholesaler || isCheckedHolderName">
              <p class="label mb-0">계좌 정보</p>
              <span class="fixed-value">
                {{ form.bank | bankShortName }} {{ form.accountNumber }}
              </span>
            </template>

            <ValidationObserver v-else v-slot="{ errors, handleSubmit }" slim>
              <form @submit.prevent="handleSubmit(checkHolderName)">
                <p class="label">계좌 정보</p>

                <div class="form-control mb-6">
                  <v-layout>
                    <v-col cols="5" class="pa-0">
                      <BankAutocomplete
                        ref="bank"
                        :bankCode.sync="form.bank"
                        :error="errors['은행']?.length > 0"
                      />
                    </v-col>
                    <v-col cols="7" class="pa-0 pl-1">
                      <ValidationProvider name="계좌 번호" rules="required">
                        <v-text-field
                          ref="accountNumber"
                          v-model="form.accountNumber"
                          outlined
                          placeholder="송금할 계좌 번호"
                          hide-details
                          :error="errors['계좌 번호']?.length > 0"
                          oninput="this.value = this.value.replace(/[^0-9.]/g, '')"
                        ></v-text-field>
                      </ValidationProvider>
                    </v-col>
                  </v-layout>

                  <v-slide-y-transition hide-on-leave>
                    <p
                      class="guide-error position--absolute"
                      :class="{ 'opacity-0': !(errors['은행']?.[0] || errors['계좌 번호']?.[0]) }"
                    >
                      {{ errors['은행']?.[0] || errors['계좌 번호']?.[0] }}
                    </p>
                  </v-slide-y-transition>
                </div>

                <v-slide-y-transition hide-on-leave>
                  <v-btn
                    v-if="!isCheckedHolderName"
                    large
                    block
                    depressed
                    height="46"
                    type="submit"
                    color="primary"
                    class="mt-2 font-size--16 font-weight-bold"
                    :disabled="errors['은행']?.length > 0 || errors['계좌 번호']?.length > 0"
                  >
                    계좌주 조회하기
                  </v-btn>
                </v-slide-y-transition>
              </form>
            </ValidationObserver>

            <p v-if="form.holderName" class="font-size--12 font-weight-bold mb-0">
              결제 계좌 이름: {{ form.holderName }}
            </p>
          </div>

          <!-- 공급사 입력 -->
          <div class="form-group">
            <p class="label" :class="{ 'mb-0': wholesalerAccount }">공급사</p>
            <div class="form-control">
              <WholesalerAutocomplete
                ref="wholesaler"
                :wholesaler-account.sync="wholesalerAccount"
              />
            </div>
          </div>
        </div>
      </v-slide-y-transition>

      <v-footer fixed class="px-0">
        <v-layout class="px">
          <v-btn
            height="52"
            color="accent"
            class="rounded-5 font--m-3"
            block
            large
            depressed
            :disabled="loading || !isCheckedHolderName || invalid"
            type="submit"
          >
            외상결제 신청하기
          </v-btn>
        </v-layout>
      </v-footer>
    </form>
  </ValidationObserver>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import { defaultApi } from '@/plugins/api/defaultApi'
import { commaFormat } from '@/filter/number'

export default {
  components: {
    CreditPayInput: () => import('@/components/credit-pay/request/form/CreditPayInput.vue'),
    WholesalerAutocomplete: () => import('@/components/credit-pay/request/form/WholesalerAutocomplete.vue'),
    BankAutocomplete: () => import('@/components/credit-pay/request/form/BankAutocomplete.vue'),
  },
  data() {
    return {
      ready: false,

      wholesalerAccount: null,

      form: {
        wholesalerName: '',
        bank: '',
        accountNumber: '',
        holderName: '',
        amount: '',
        depositName: '',
      },
      loading: false,
    }
  },
  mounted() {
    this.ready = true

    setTimeout(() => {
      this.navigateToWholesaler()
    }, 100)
  },
  watch: {
    /**
     * 공급사 변경하는경우 계좌 정보까지 초기화
     */
    'wholesalerAccount'(v) {
      this.form.wholesalerName = v?.wholesaler?.name
      this.form.bank = v?.bank
      this.form.accountNumber = v?.accountNumber
      this.form.holderName = v?.holderName

      if (!v?.accountNumber) {
        this.navigateToBankSelect()
        return
      }

      this.navigateToAmountInput()
    },

    'form.bank'(v) {
      if (!v) {
        return
      }
      if (this.wholesalerAccount.uuid == null) {
        this.navigateToAccountNumberInput()
      }
    },
  },
  computed: {
    ...mapState('creditPay/wholesaler', ['wholesalers']),
    ...mapGetters('creditPay/wholesaler', ['getWholesaler']),

    isExistWholesaler() {
      return this.wholesalerAccount?.uuid
    },
    isCheckedHolderName() {
      if (this.form.holderName?.length > 0 || this.isExistWholesaler) {
        return true
      }
      return false
    },
    paymentForm() {
      return {
        wholesaler: {
          name: this.form.wholesalerName,
        },
        accountNumber: this.form.accountNumber,
        bank: this.form.bank,
        holderName: this.form.holderName,
        depositName: this.form.depositName,
        amount: this.form.amount.replace(/[^0-9.]/g, ''),
      }
    },
  },
  methods: {
    ...mapActions('fundaPaygate', ['getAccountHolderName']),

    navigateToWholesaler() {
      setTimeout(() => {
        this.$refs.wholesaler?.$refs?.autocomplete?.focus()
        this.$refs.wholesaler?.$refs?.autocomplete?.activateMenu()
      }, 100)
    },

    navigateToBankSelect() {
      setTimeout(() => {
        this.$refs.bank?.$refs?.select?.focus()
        this.$refs.bank?.$refs?.select?.activateMenu()
      }, 100)
    },

    navigateToAccountNumberInput() {
      setTimeout(() => {
        this.$refs.accountNumber?.focus()
      }, 100)
    },

    navigateToAmountInput() {
      setTimeout(() => {
        this.$refs.amount?.$refs?.input?.focus()
      }, 300)
    },

    async checkHolderName() {
      const holderName = await this.getAccountHolderName({
        bank: this.form.bank,
        accountNumber: this.form.accountNumber,
      })

      const { isConfirmed } = await this.$swal.basic.confirm({
        html: `예금주 : ${holderName}<br/>결제하실 계좌가 맞나요?`,
        cancelButtonText: '다시 입력하기',
      })

      if (isConfirmed) {
        this.form.holderName = holderName
        this.navigateToAmountInput()
      } else {
        this.form.bank = null
        this.form.accountNumber = null
        this.navigateToBankSelect()
      }
    },

    async submit() {
      if (this.loading) {
        return
      }

      try {
        this.loading = true

        const { isConfirmed } = await this.$swal.basic.confirm({
          html: `‘${this.form.wholesalerName}’에 ${commaFormat(
            this.form.amount,
          )}원을 외상결제 하시겠습니까?`,
        })
        if (!isConfirmed) {
          return
        }

        // 신청
        await defaultApi.postCreditPaysRequest(this.paymentForm)

        await this.$swal.basic.alert({
          html: '신청이 완료되었습니다.',
        })
        this.$router.push({ name: 'credit-pay' })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.px {
  padding: 0 12px;
}
</style>
